import type { VisitTypeId } from '../services/api/types.gen';
import type { DisciplineType } from '../types/worksheetGoals.types';

/**
 * Visit types shown in pickers only when `versioned-form-schemas` is enabled.
 * The OASIS form uses the pinned schema loader for these only with that flag;
 * without it, opening the visit would follow the legacy SOC schema path.
 */
export const VISIT_TYPES_REQUIRING_VERSIONED_FORM_SCHEMAS =
  new Set<VisitTypeId>(['OASIS_E2_START_OF_CARE_ENHANCED']);

export const VISIT_TYPE_DISCIPLINE_MAP: Record<VisitTypeId, DisciplineType> = {
  OASIS_E2_START_OF_CARE: 'SN',
  OASIS_E2_START_OF_CARE_ENHANCED: 'SN',
  OASIS_E2_RESUMPTION_OF_CARE: 'SN',
  OASIS_E2_RECERTIFICATION: 'SN',
  OASIS_E2_DISCHARGE: 'SN',
  OASIS_E2_START_OF_CARE_PT: 'PT',
  OASIS_E2_RESUMPTION_OF_CARE_PT: 'PT',
  OASIS_E2_RECERTIFICATION_PT: 'PT',
  OASIS_E2_DISCHARGE_PT: 'PT',
  OASIS_E2_START_OF_CARE_OT: 'OT',
  OASIS_E2_RESUMPTION_OF_CARE_OT: 'OT',
  OASIS_E2_RECERTIFICATION_OT: 'OT',
  OASIS_E2_DISCHARGE_OT: 'OT',
  OASIS_E2_START_OF_CARE_ST: 'ST',
  OASIS_E2_RESUMPTION_OF_CARE_ST: 'ST',
  OASIS_E2_RECERTIFICATION_ST: 'ST',
  OASIS_E2_DISCHARGE_ST: 'ST',
  SKILLED_NURSING_ASSESSMENT: 'SN',
  SKILLED_NURSING_EVALUATION: 'SN',
  LPN_LVN_SKILLED_NURSING_VISIT: 'SN',
  RN_SKILLED_NURSING_VISIT: 'SN',
  PT_EVALUATION: 'PT',
  PT_RE_EVALUATION: 'PT',
  PT_VISIT: 'PT',
  PT_TREATMENT_VISIT: 'PT',
  PT_DISCHARGE: 'PT',
  PT_DISCHARGE_VISIT: 'PT',
  OT_EVALUATION: 'OT',
  OT_RE_EVALUATION: 'OT',
  OT_VISIT: 'OT',
  OT_TREATMENT_VISIT: 'OT',
  OT_DISCHARGE: 'OT',
  OT_DISCHARGE_VISIT: 'OT',
  ST_EVALUATION: 'ST',
  ST_RE_EVALUATION: 'ST',
  ST_VISIT: 'ST',
  ST_TREATMENT_VISIT: 'ST',
  ST_DISCHARGE: 'ST',
  ST_DISCHARGE_VISIT: 'ST',
  HOME_HEALTH_AIDE_VISIT: 'SN',
  HHA_STANDARD_VISIT: 'SN',
  // Order types
  CMS_485: 'SN',
  FREQUENCY_DISCONTINUE_ORDER: 'SN',
  FREQUENCY_ORDER: 'SN',
  PRN_ORDER: 'SN',
  CMS_486: 'SN',
  PHYSICIAN_ORDER: 'SN',
  POST_HOSPITAL_ORDER: 'SN',
  TP_1: 'SN',
  TP_2: 'SN',
  SCIC_ORDER: 'SN',
  F2F_ENCOUNTER: 'SN',
  MEDICAID_POC: 'SN',
  PLAN_OF_CARE_ADDENDUM: 'SN',
};

/** Visit types that support goal addition (SOC, ROC, REC, EVAL across all disciplines). */
const GOAL_ELIGIBLE_VISIT_TYPES: ReadonlySet<VisitTypeId> =
  new Set<VisitTypeId>([
    // SOC
    'OASIS_E2_START_OF_CARE',
    'OASIS_E2_START_OF_CARE_ENHANCED',
    'OASIS_E2_START_OF_CARE_PT',
    'OASIS_E2_START_OF_CARE_OT',
    'OASIS_E2_START_OF_CARE_ST',
    // ROC
    'OASIS_E2_RESUMPTION_OF_CARE',
    'OASIS_E2_RESUMPTION_OF_CARE_PT',
    'OASIS_E2_RESUMPTION_OF_CARE_OT',
    'OASIS_E2_RESUMPTION_OF_CARE_ST',
    // REC
    'OASIS_E2_RECERTIFICATION',
    'OASIS_E2_RECERTIFICATION_PT',
    'OASIS_E2_RECERTIFICATION_OT',
    'OASIS_E2_RECERTIFICATION_ST',
    // EVAL
    'SKILLED_NURSING_ASSESSMENT',
    'SKILLED_NURSING_EVALUATION',
    'PT_EVALUATION',
    'PT_RE_EVALUATION',
    'OT_EVALUATION',
    'OT_RE_EVALUATION',
    'ST_EVALUATION',
    'ST_RE_EVALUATION',
  ]);

export const canAddGoals = (visitType: VisitTypeId): boolean =>
  GOAL_ELIGIBLE_VISIT_TYPES.has(visitType);

export const isSNDiscipline = (visitType: VisitTypeId): boolean =>
  VISIT_TYPE_DISCIPLINE_MAP[visitType] === 'SN';

/** Order type visit IDs. */
export const ORDER_TYPE_IDS: ReadonlySet<VisitTypeId> = new Set<VisitTypeId>([
  'CMS_485',
  'FREQUENCY_DISCONTINUE_ORDER',
  'FREQUENCY_ORDER',
  'PRN_ORDER',
  'CMS_486',
  'PHYSICIAN_ORDER',
  'POST_HOSPITAL_ORDER',
  'TP_1',
  'TP_2',
  'SCIC_ORDER',
  'F2F_ENCOUNTER',
  'MEDICAID_POC',
  'PLAN_OF_CARE_ADDENDUM',
]);

export const isOrderType = (visitType: VisitTypeId): boolean =>
  ORDER_TYPE_IDS.has(visitType);
